import { Handle, Position } from '@xyflow/react'
import { useState, useRef } from 'react'
import { ChevronDown, ChevronUp, MoreVertical } from 'lucide-react'
import FloatingMenu from '../../components/shared/FloatingMenu'
import { STATUS_LABELS, STATUS_COLORS, LABELS } from './labels'
import { NavigatorAvatarMarker } from './NavigatorIndicator'
import {
  CARD_WIDTH,
  cardAssignees,
  cardResponsibilities,
  hiddenAssigneeCount,
  hiddenResponsibilityCount,
  seatCardHeight,
  showsStatusRow,
} from './cardMetrics'
import type { CardDensity } from './cardMetrics'
import type { DropHint } from './dropTargets'
import type { SeatDetail } from './types'

export type { DropHint }

interface Data {
  seat: SeatDetail
  density: CardDensity
  isCollapsed: boolean
  hasChildren: boolean
  dimmed: boolean
  highlighted: boolean
  draggable: boolean
  dropHint: DropHint | null
  /**
   * This card has just left the chart — a branch was collapsed, or the role was
   * removed — and is only still on screen to fade out. It is a picture: no
   * hover, no pointer events, and the layout has already forgotten it.
   */
  leaving?: boolean
  displayedHeight?: number
  // Pure view state from the toolbar toggle. It only adds a corner marker to
  // the owner avatars — never the node size, layout or content.
  showNavigatorStatus?: boolean
  onClickSeat: (seat: SeatDetail, event?: { shiftKey?: boolean }) => void
  onToggleCollapse: (id: number) => void
  onAction: (action: string, seat: SeatDetail) => void
  onConnectStart: (seatId: number, direction: 'top' | 'bottom', clientX: number, clientY: number) => void
}

export function SeatNode({ data, dragging, selected }: { data: Data; dragging?: boolean; selected?: boolean }) {
  const {
    seat, density, isCollapsed, hasChildren, dimmed, highlighted, draggable, dropHint, leaving,
    showNavigatorStatus, onClickSeat, onToggleCollapse, onAction, onConnectStart,
  } = data
  const canEditSeat = seat.can_edit
  const [menuOpen, setMenuOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const colors = STATUS_COLORS[seat.seat_status] || STATUS_COLORS.open
  const visibleAssignees = cardAssignees(seat)
  const extraAssignees = hiddenAssigneeCount(seat)
  const visibleTags = seat.tags.slice(0, 2)
  const extraTags = Math.max(0, seat.tags.length - 2)
  const isExpanded = density === 'expanded'
  const visibleResponsibilities = cardResponsibilities(seat)
  const hiddenResponsibilities = hiddenResponsibilityCount(seat)

  const menuItems = [
    canEditSeat && { key: 'add-below', label: `Add ${LABELS.seat.toLowerCase()} below` },
    canEditSeat && { key: 'add-right', label: `Add ${LABELS.seat.toLowerCase()} to right` },
    canEditSeat && { key: 'move-up', label: 'Move up' },
    canEditSeat && { key: 'move-down', label: 'Move down' },
    canEditSeat && { key: 'promote', label: 'Promote' },
    canEditSeat && { key: 'demote', label: 'Demote' },
    canEditSeat && { key: 'move-to', label: 'Move to…' },
    canEditSeat && { key: 'duplicate', label: 'Duplicate' },
    canEditSeat && { key: 'delete', label: `Delete ${LABELS.seat.toLowerCase()}`, danger: true },
  ].filter(Boolean) as { key: string; label: string; danger?: boolean }[]

  const borderClass = dropHint === 'invalid'
    ? 'border-pinnacle-red ring-2 ring-pinnacle-red/40'
    : dropHint === 'child'
      ? 'border-sea ring-2 ring-sea/40'
      // Being part of a group outranks matching the search: the selection is
      // what the next click is about to move.
      : selected
        ? 'border-sea ring-2 ring-sea'
        : highlighted
          ? 'border-pinnacle-red ring-2 ring-pinnacle-red/30'
          : 'border-stone'

  /**
   * Hover is the quietest state on the card, and it only exists where it is
   * honest: a card someone can actually pick up. A viewer's chart, and the
   * unsaved Auto Layout preview, are pictures — a card that rises under the
   * pointer there would promise a gesture that goes nowhere.
   *
   * It is also the LOWEST state: a drop hint, a selection or a search match all
   * say something about what is about to happen, so a card wearing one of them
   * keeps its own border and elevation and the pointer changes nothing.
   */
  const restingAppearance = !dropHint && !selected && !highlighted && !dimmed && !dragging && !leaving
  const hoverable = restingAppearance && draggable

  // Elevation is shadow-only: independently translating the inner card would
  // move its visible bounds away from the connector endpoints during reflow.
  const liftClass = dragging
    ? 'shadow-drag'
    : hoverable
      ? 'shadow-card hover:shadow-card-hover'
      : 'shadow-card'

  return (
    <div
      className={[
        'chart-card relative bg-white rounded-card border',
        liftClass,
        borderClass,
        hoverable ? 'hover:border-charcoal/30' : '',
        dimmed ? 'opacity-30' : '',
        leaving ? 'chart-card-leaving' : '',
        // A card that has just appeared — a branch expanded, a role added —
        // fades in rather than popping into the reflowing tree. A dimmed card
        // is left out: it would fade in to full strength and then drop back.
        !leaving && !dimmed ? 'chart-card-enter' : '',
        draggable ? 'cursor-grab active:cursor-grabbing' : '',
        'transition-[opacity,box-shadow,transform,border-color] duration-150 ease-out',
      ].filter(Boolean).join(' ')}
      style={{ width: CARD_WIDTH, height: data.displayedHeight ?? seatCardHeight(seat, { density, hasChildren }) }}
      data-testid={`seat-card-${seat.id}`}
      data-density={density}
      data-dragging={dragging ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      data-drop-hint={dropHint || undefined}
      data-hoverable={hoverable ? 'true' : undefined}
      data-leaving={leaving ? 'true' : undefined}
    >
      <Handle type="target" position={Position.Top} className="!bg-stone !border-stone !pointer-events-none" />
      {draggable && !leaving && (
        <button
          type="button"
          aria-label={`Connect from top of ${seat.name}`}
          data-testid={`connect-handle-top-${seat.id}`}
          className="nodrag nopan absolute left-1/2 top-0 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-terrain bg-white shadow-card cursor-crosshair hover:bg-sand"
          onPointerDown={event => {
            event.preventDefault()
            event.stopPropagation()
            onConnectStart(seat.id, 'top', event.clientX, event.clientY)
          }}
          onClick={event => event.stopPropagation()}
        />
      )}
      <div className="p-3 overflow-hidden cursor-pointer" style={{ maxHeight: `calc(100% - ${hasChildren ? 26 : 0}px)` }} onClick={event => onClickSeat(seat, event)}>
        <div className="flex items-start justify-between gap-2 h-6">
          <h4 className="font-semibold text-text-primary text-base leading-tight flex-1 truncate">{seat.name}</h4>
          {/* A viewer has no actions here, so there is no trigger to press:
              rendering one would open an empty popup. */}
          {menuItems.length > 0 && (
            <div className="relative">
              <button
                ref={triggerRef}
                onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
                className="p-1 hover:bg-stone/40 rounded text-text-secondary"
                aria-label="Role actions"
              >
                <MoreVertical size={16} />
              </button>
              <FloatingMenu
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchorRef={triggerRef}
                width={200}
                style={{ minWidth: 200 }}
                className="bg-white shadow-elevated rounded-card border border-stone py-1"
                onClick={(e) => e.stopPropagation()}
              >
                {menuItems.map(item => (
                  <button
                    key={item.key}
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onAction(item.key, seat) }}
                    className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-stone/30 ${item.danger ? 'text-pinnacle-red' : 'text-text-primary'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </FloatingMenu>
            </div>
          )}
        </div>

        {/* A filled role shows no badge — its owners already say so. The row is
            dropped whole, not hidden, so no empty band is left behind, and
            showsStatusRow keeps seatCardHeight in step. */}
        {showsStatusRow(seat) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 h-5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${colors.bg} ${colors.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
              {STATUS_LABELS[seat.seat_status]}
            </span>
          </div>
        )}

        {/* Owners read as names, at both densities: the chart is meant to
            answer "who owns this" without hovering a circle of initials.
            Mirrored by ownersBlockHeight, which the layout sizes cards from. */}
        {visibleAssignees.length > 0 && (
          <div className="mt-2" data-testid={`seat-card-${seat.id}-owners`}>
            <ul className="flex flex-col gap-1">
              {/* allow-unsorted-people: do NOT alphabetize. Seat assignments
                  read primary holder first, then the other holders. */}
              {visibleAssignees.map(a => (
                <li key={a.id} className="flex items-center gap-1.5 h-6">
                  <span className="relative shrink-0">
                    <span
                      className={`w-6 h-6 rounded-full text-[10px] font-semibold flex items-center justify-center ${a.is_internal ? 'bg-sea text-white' : 'bg-terrain text-white'}`}
                      aria-hidden="true"
                    >
                      {a.initials}
                    </span>
                    {showNavigatorStatus && (
                      <NavigatorAvatarMarker status={a.navigator_status} name={a.display_name} />
                    )}
                  </span>
                  {/* A very long name truncates inside the card's fixed width
                      and keeps the whole name in its tooltip. */}
                  <span
                    data-testid="owner-name"
                    className="min-w-0 flex-1 text-xs leading-5 text-text-primary truncate"
                    title={a.display_name}
                  >
                    {a.display_name}
                  </span>
                </li>
              ))}
              {extraAssignees > 0 && (
                <li className="text-[11px] leading-4 text-text-secondary">+{extraAssignees} more</li>
              )}
            </ul>
          </div>
        )}

        {isExpanded && (
          <div className="mt-2" data-testid={`seat-card-${seat.id}-responsibilities`}>
            {/* Obsession names have to read on the card at the zoom the chart
                opens at, so this list is body-sized type, not fine print. Its
                line height is mirrored by RESPONSIBILITY_LINE_H. */}
            {visibleResponsibilities.length === 0 ? (
              <p className="text-xs leading-5 text-text-secondary italic truncate">
                No {LABELS.responsibilities.toLowerCase()} yet
              </p>
            ) : (
              <ul>
                {/* allow-unsorted-people: saved obsession order, never alphabetized. */}
                {visibleResponsibilities.map(r => (
                  <li key={r.id} className="text-xs leading-5 text-text-primary truncate" title={r.title}>
                    • {r.title}
                  </li>
                ))}
                {hiddenResponsibilities > 0 && (
                  <li className="text-xs leading-5 text-text-secondary">+{hiddenResponsibilities} more</li>
                )}
              </ul>
            )}
          </div>
        )}

        {isExpanded && (visibleTags.length > 0 || extraTags > 0) && (
          <div className="flex items-center gap-1 mt-2 h-5 flex-wrap overflow-hidden">
            {visibleTags.map(t => (
              <span key={t.id} className="text-[10px] leading-4 px-1.5 py-0.5 rounded-badge bg-stone text-text-primary">{t.name}</span>
            ))}
            {extraTags > 0 && <span className="text-[10px] leading-4 text-text-muted">+{extraTags}</span>}
          </div>
        )}
      </div>

      {hasChildren && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleCollapse(seat.id) }}
          className="absolute bottom-0 left-0 right-0 h-[26px] px-3 border-t border-stone text-text-secondary hover:bg-stone/30 flex items-center justify-center gap-1 text-xs"
        >
          {isCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          {isCollapsed ? 'Expand' : 'Collapse'}
        </button>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-stone !border-stone !pointer-events-none" />
      {draggable && !leaving && (
        <button
          type="button"
          aria-label={`Connect from bottom of ${seat.name}`}
          data-testid={`connect-handle-bottom-${seat.id}`}
          className="nodrag nopan absolute left-1/2 bottom-0 z-10 h-5 w-5 -translate-x-1/2 translate-y-1/2 rounded-full border-2 border-terrain bg-white shadow-card cursor-crosshair hover:bg-sand"
          onPointerDown={event => {
            event.preventDefault()
            event.stopPropagation()
            onConnectStart(seat.id, 'bottom', event.clientX, event.clientY)
          }}
          onClick={event => event.stopPropagation()}
        />
      )}
    </div>
  )
}
